/**
 * API Error Types
 *
 * Types for handling backend errors on the frontend.
 * Compatible with Convex CustomError format.
 */

import type { DomainErrorCode } from "@/packages/domains/shared";
import type { CustomError } from "@/packages/types";

/**
 * Backend error structure (alias for CustomError from packages/types).
 * Contains all metadata from the backend for detailed error display.
 */
export type BackendErrorData = CustomError;

/**
 * Backend error class (matches Convex CustomError structure).
 * Extends Error for proper stack traces and instanceof checks.
 * Contains all backend error metadata for detailed error display.
 */
export class ApiError extends Error {
  readonly code: DomainErrorCode;
  readonly severity?: BackendErrorData["severity"];
  readonly category?: BackendErrorData["category"];
  readonly details?: Record<string, unknown>;
  readonly timestamp?: string;
  readonly requestId?: string;
  readonly userId?: string;

  constructor(data: BackendErrorData) {
    super(data.message);
    this.name = data.name ?? "ApiError";
    this.code = data.code;
    this.severity = data.severity;
    this.category = data.category;
    this.details = data.details;
    this.timestamp = data.timestamp;
    this.requestId = data.requestId;
    this.userId = data.userId;

    // Maintain proper stack trace (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  /**
   * Returns all error data for display in error modals, logging, etc.
   */
  toJSON(): BackendErrorData {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      severity: this.severity,
      category: this.category,
      details: this.details,
      timestamp: this.timestamp,
      requestId: this.requestId,
      userId: this.userId,
    };
  }
}

/**
 * Interface for duck-typing API errors (useful for objects that aren't ApiError instances)
 */
// TODO FIX: We have an CustomError in the convex -> all types / constants should be in shared folder
export interface ApiErrorLike {
  code: DomainErrorCode;
  message: string;
  name?: string;
  severity?: BackendErrorData["severity"];
  category?: BackendErrorData["category"];
  details?: Record<string, unknown>;
  timestamp?: string;
  requestId?: string;
  userId?: string;
}

/**
 * Type guard to check if an error is an ApiError or has ApiError-like structure
 */
export function isApiError(error: unknown): error is ApiError | ApiErrorLike {
  if (error instanceof ApiError) {
    return true;
  }
  return (
    error !== null &&
    typeof error === "object" &&
    "code" in error &&
    "message" in error &&
    typeof (error as ApiErrorLike).code === "string" &&
    typeof (error as ApiErrorLike).message === "string"
  );
}

/**
 * Extract error code from any error type
 */
export function getErrorCode(error: unknown): DomainErrorCode | undefined {
  if (isApiError(error)) {
    return error.code;
  }
  return undefined;
}

/**
 * Extract error message from any error type
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred";
}

/**
 * Extract error details from any error type
 */
export function getErrorDetails(
  error: unknown,
): Record<string, unknown> | undefined {
  if (isApiError(error)) {
    return error.details;
  }
  return undefined;
}

/**
 * Create an ApiError from a backend error response
 */
export function createApiError(backendError: ApiErrorLike): ApiError {
  return new ApiError({
    name: backendError.name ?? "ApiError",
    message: backendError.message,
    code: backendError.code,
    severity: backendError.severity,
    category: backendError.category,
    details: backendError.details,
    timestamp: backendError.timestamp,
    requestId: backendError.requestId,
    userId: backendError.userId,
  });
}

export type { DomainErrorCode };
